import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { ChangeRequestsPanel } from './ChangeRequestsPanel';
import { useAuth } from '../context/AuthContext';

const FIELDS = [
  { key: 'alert_email', label: 'Correo de alertas', type: 'email', help: 'Destino de las alertas de infraestructura.' },
  { key: 'check_interval_minutes', label: 'Intervalo de comprobación (min)', min: 5, max: 1440, help: 'Mínimo 5 (frecuencia del cron).' },
  { key: 'failures_to_open', label: 'Fallas seguidas para abrir incidente', min: 1, max: 20, help: 'Una sola falla se trata como transitoria.' },
  { key: 'reminder_hours', label: 'Recordatorio de incidente abierto (h)', min: 1, max: 168, help: 'Evita correos repetidos.' },
  { key: 'notify_recovery', label: 'Avisar recuperación (1 = sí, 0 = no)', min: 0, max: 1, help: '' },
  { key: 'dashboard_refresh_seconds', label: 'Actualización del dashboard (s)', min: 15, max: 3600, help: 'Solo afecta a la pantalla, no a los correos.' },
];

export const InfraSettingsPanel = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { can } = useAuth();

  useEffect(() => {
    api.get('/admin/infra/health').then((r) => setForm(Object.fromEntries(FIELDS.map((f) => [f.key, String(r.data.settings[f.key])]))))
      .catch(() => toast.error('No se pudo cargar la configuración'));
  }, []);

  const save = async () => {
    const body = {};
    for (const f of FIELDS) {
      if (f.type === 'email') { body[f.key] = form[f.key].trim(); continue; }
      const v = Number(form[f.key]);
      if (!Number.isInteger(v) || v < f.min || v > f.max) { toast.error(`${f.label}: entre ${f.min} y ${f.max}`); return; }
      body[f.key] = v;
    }
    setSaving(true);
    try {
      const r = await api.put('/admin/infra/settings', body);
      toast.success(r.data.message || 'Cambio propuesto');
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); } finally { setSaving(false); }
  };

  if (!form) return <div className="text-gray-400 py-10 text-center">Cargando…</div>;
  return (
    <>
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 max-w-3xl" data-testid="infra-settings-form">
      <h3 className="text-white font-semibold mb-1">Alertas de infraestructura</h3>
      <p className="text-gray-500 text-xs mb-4">Los cambios se envían como propuesta y solo se aplican cuando un SuperAdmin los aprueba con su código MFA.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-gray-300 text-sm">{f.label}</span>
            <input type={f.type || 'number'} min={f.min} max={f.max} value={form[f.key]}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              className="mt-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white w-full"
              data-testid={`infra-setting-${f.key.replace(/_/g, '-')}`} />
            {f.help && <span className="text-gray-500 text-xs">{f.help}</span>}
          </label>
        ))}
      </div>
      {can('infra.propose') && (
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid="infra-settings-save">
          <Save className="w-4 h-4 mr-1" /> Proponer cambio
        </Button>
      )}
    </div>
    <ChangeRequestsPanel refreshKey={refreshKey} />
    </>
  );
};

export default InfraSettingsPanel;
